import React, { useMemo, useState, useRef } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { skKrajeGeo } from "../../../../store/helpers/skKrajeGeo";

const FROM = [212, 217, 226];

function hexToRgb(hex) {
    const clean = hex.replace("#", "");
    return [
        parseInt(clean.substring(0, 2), 16),
        parseInt(clean.substring(2, 4), 16),
        parseInt(clean.substring(4, 6), 16),
    ];
}

function lerp(a, b, t) {
    return Math.round(a + (b - a) * t);
}

function RegionMap({ values = {}, selected, onSelect, valueLabel = "", formatValue, toColor = "#d60c8c" }) {
    const [hovered, setHovered] = useState(null);
    const [pointer, setPointer] = useState({ x: 0, y: 0 });
    const wrapperRef = useRef(null);

    const to = useMemo(() => hexToRgb(toColor), [toColor]);

    const max = useMemo(() => {
        const nums = Object.values(values).map((v) => Number(v) || 0);
        return Math.max(1, ...nums);
    }, [values]);

    const colorFor = (t) => {
        const x = Math.max(0, Math.min(1, t));
        return `rgb(${lerp(FROM[0], to[0], x)}, ${lerp(FROM[1], to[1], x)}, ${lerp(FROM[2], to[2], x)})`;
    };

    const fmt = formatValue || ((v) => v);
    const tooltipName = hovered;
    const tooltipValue = tooltipName != null ? Number(values[tooltipName]) || 0 : null;
    const hasSelection = selected != null;
    const selectedValue = hasSelection ? Number(values[selected]) || 0 : null;

    const handleMove = (event) => {
        const rect = wrapperRef.current?.getBoundingClientRect();
        if (!rect) return;
        setPointer({ x: event.clientX - rect.left, y: event.clientY - rect.top });
    };

    return (
        <div ref={wrapperRef} onMouseMove={handleMove} style={{ position: "relative", width: "100%" }}>
            {hasSelection && (
                <div style={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    zIndex: 4,
                    background: "#fff",
                    border: `2px solid ${toColor}`,
                    borderRadius: 10,
                    padding: "8px 14px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                }}>
                    <div style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#999" }}>Vybraný kraj</div>
                    <div style={{ fontWeight: "bold", fontSize: "1rem", color: "#333" }}>{selected}</div>
                    <div style={{ color: "#666", fontSize: "0.85rem" }}>{valueLabel}: <strong style={{ color: toColor }}>{fmt(selectedValue)}</strong></div>
                </div>
            )}

            {tooltipName && tooltipName !== selected && (
                <div style={{
                    position: "absolute",
                    left: pointer.x + 14,
                    top: pointer.y + 14,
                    zIndex: 5,
                    pointerEvents: "none",
                    background: "rgba(33,37,41,0.95)",
                    color: "#fff",
                    padding: "8px 12px",
                    borderRadius: 8,
                    fontSize: "0.85rem",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
                    whiteSpace: "nowrap",
                }}>
                    <div style={{ fontWeight: "bold", marginBottom: 2 }}>{tooltipName}</div>
                    <div style={{ opacity: 0.85 }}>{valueLabel}: <strong>{fmt(tooltipValue)}</strong></div>
                </div>
            )}

            <ComposableMap
                projection="geoMercator"
                projectionConfig={{ center: [19.55, 48.72], scale: 5400 }}
                width={800}
                height={420}
                style={{ width: "100%", height: "auto" }}
            >
                <Geographies geography={skKrajeGeo}>
                    {({ geographies }) => {
                        const ordered = [...geographies].sort((a, b) => {
                            if (a.properties.name === selected) return 1;
                            if (b.properties.name === selected) return -1;
                            return 0;
                        });
                        return ordered.map((geo) => {
                            const name = geo.properties.name;
                            const value = Number(values[name]) || 0;
                            const isSelected = selected === name;
                            const dim = hasSelection && !isSelected ? 0.4 : 1;
                            return (
                                <Geography
                                    key={geo.rsmKey}
                                    geography={geo}
                                    onMouseEnter={() => setHovered(name)}
                                    onMouseLeave={() => setHovered(null)}
                                    onClick={() => onSelect && onSelect(isSelected ? null : name)}
                                    style={{
                                        default: {
                                            fill: colorFor(value / max),
                                            fillOpacity: dim,
                                            stroke: isSelected ? "#0b1f2a" : "#ffffff",
                                            strokeWidth: isSelected ? 2.5 : 0.8,
                                            outline: "none",
                                            cursor: "pointer",
                                            filter: isSelected ? "drop-shadow(0 0 6px rgba(0,0,0,0.45))" : "none",
                                            transition: "fill-opacity 0.2s",
                                        },
                                        hover: {
                                            fill: colorFor(value / max + 0.18),
                                            fillOpacity: 1,
                                            stroke: "#0b1f2a",
                                            strokeWidth: isSelected ? 2.5 : 1.6,
                                            outline: "none",
                                            cursor: "pointer",
                                            filter: isSelected ? "drop-shadow(0 0 6px rgba(0,0,0,0.45))" : "none",
                                        },
                                        pressed: {
                                            fill: colorFor(value / max),
                                            stroke: "#0b1f2a",
                                            strokeWidth: 2.5,
                                            outline: "none",
                                        },
                                    }}
                                />
                            );
                        });
                    }}
                </Geographies>
            </ComposableMap>

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, color: "#999", fontSize: "0.78rem" }}>
                <span>0</span>
                <div style={{ flex: 1, height: 8, borderRadius: 4, background: `linear-gradient(90deg, ${colorFor(0)}, ${colorFor(1)})` }} />
                <span>{valueLabel} (max {fmt(max)})</span>
            </div>
        </div>
    );
}

export default RegionMap;
